import type { SimplePeriodType, CustomDateRange } from '../../../../shared/helpers/date.functions'
import { daysDiff } from '../../../../shared/helpers/date.functions'

export type Resolution = 'daily' | 'weekly' | 'monthly'
export type Grouping = 'daily' | 'weekly' | 'monthly'

/**
 * Computes the approximate span in days for a given period.
 */
export const getSpanDays = (
  period: SimplePeriodType,
  customRange: CustomDateRange | undefined,
  today = new Date()
): number => {
  switch (period) {
    case 'last_7_days':
      return 7
    case 'last_30_days':
      return 30
    case 'last_90_days':
      return 90
    case 'last_month': {
      const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      return daysDiff(firstOfLastMonth, firstOfThisMonth)
    }
    case 'last_year':
      return 366
    case 'all_time': {
      const allTimeStart = new Date(2020, 0, 1)
      return daysDiff(allTimeStart, today)
    }
    case 'custom': {
      if (!customRange) return 30
      return daysDiff(customRange.from, customRange.to)
    }
    default:
      return 30
  }
}

/**
 * Returns available groupings based on the span of the selected period.
 */
export const getAvailableGroupings = (
  period: SimplePeriodType,
  customRange: CustomDateRange | undefined,
  today = new Date()
): Grouping[] => {
  const span = getSpanDays(period, customRange, today)

  if (span < 28) return ['daily', 'weekly']
  if (span <= 90) return ['daily', 'weekly', 'monthly']
  if (span <= 365) return ['weekly', 'monthly']
  return ['monthly']
}

/**
 * Returns the default grouping for a given period.
 * Named periods have explicit defaults; custom uses span-based logic.
 */
export const getDefaultGrouping = (
  period: SimplePeriodType,
  customRange: CustomDateRange | undefined,
  today = new Date()
): Grouping => {
  switch (period) {
    case 'last_7_days':
      return 'daily'
    case 'last_30_days':
      return 'weekly'
    case 'last_90_days':
      return 'monthly'
    case 'last_month':
      return 'weekly'
    case 'last_year':
      return 'monthly'
    case 'all_time':
      return 'monthly'
    case 'custom': {
      const span = getSpanDays('custom', customRange, today)
      if (span <= 7) return 'daily'
      if (span <= 45) return 'weekly'
      return 'monthly'
    }
    default:
      return 'daily'
  }
}
